import HorizontalLayout from "@legacy/core/components/HorizontalLayout"
import PureComponentWrapper from "@legacy/core/components/PureComponentWrapper"
import BasicDisplayField from "@legacy/core/fields/BasicDisplayField"
import BasicSelectField from "@legacy/core/fields/BasicSelectField"
import PercentField from "@legacy/core/fields/PercentField"
import PriceField from "@legacy/core/fields/PriceField"
import SearchCreateSelectField from "@legacy/core/fields/SearchCreateSelectField"
import { PriceStrategies } from "@legacy/core/utils/enums"
import { valueIsDefined } from "@legacy/core/utils/utils"
import PriceBookItemSearchOrCreateSelect, {
    priceBookItemToPriceBookItemOption,
} from "@legacy/pricebook/inputs/PriceBookItemSearchOrCreateSelect"
import { Component, Fragment } from "react"

import { PriceBookItemTypes } from "@constants/choices"
import { PRICEBOOK_ITEM_TYPE_MAP } from "@constants/mappings"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import { AccessCheck } from "@particles/index"
import ButtonGroup from "../../core/buttons/ButtonGroup"
import ButtonGroupRow from "../../core/buttons/ButtonGroupRow"
import UniversalButton from "../../core/buttons/UniversalButton"

const PRICE_STRATEGY_CHOICES = [
    {"value": PriceStrategies.manual, "label": "Manual"},
    {"value": PriceStrategies.fixed_adjustment, "label": "Fixed Markup"},
    {"value": PriceStrategies.percent_adjustment, "label": "Percent Markup"},
]

class InlinePriceOverrideForm extends Component {
    constructor(props) {
        super(props)

        this.state = {
            selectedPriceBookItem: null,
            currencyCode: window.CURRENCY_CODE,
            languageCode: window.LANGUAGE_CODE,
        }

        window.onpopstate = (event) => {
            if (event.state !== null && Object.keys(event.state).length) {
                this.setState(event.state)
            }
        }
    }

    renderButtons = () => {
        const { item, isNewAddition, onFormDataChange, requestAction } = this.props

        return (
            <ButtonGroup>
                {isNewAddition ? (
                    <AccessCheck permissions={{
                        pricebook_edit_permission: PERMISSION_LEVEL.FULL
                    }}>
                        <ButtonGroupRow>
                            <UniversalButton type="primary" text="Save" handler={(event) => requestAction("ITEM_ADD")} />
                        </ButtonGroupRow>
                    </AccessCheck>
                ) : (
                    <AccessCheck permissions={{
                        pricebook_edit_permission: PERMISSION_LEVEL.FULL
                    }}>
                        <ButtonGroupRow>
                            <UniversalButton type="primary" text="Save" handler={(event) => requestAction("ITEM_SAVE")} />
                        </ButtonGroupRow>
                    </AccessCheck>
                )}
                {isNewAddition ? (
                    <ButtonGroupRow>
                        <UniversalButton
                            type="secondary"
                            text="Cancel"
                            handler={(event) => requestAction("ITEM_CANCEL_ADD")}
                        />
                    </ButtonGroupRow>
                ) : (
                    <Fragment>
                        <ButtonGroupRow>
                            <UniversalButton
                                type="secondary"
                                text="Cancel Edits"
                                handler={(event) => requestAction("ITEM_CANCEL_EDITS")}
                            />
                        </ButtonGroupRow>
                        <AccessCheck permissions={{
                            pricebook_delete_permission: PERMISSION_LEVEL.FULL
                        }}>
                            <ButtonGroupRow>
                                <UniversalButton
                                    type="danger"
                                    text="Delete Override"
                                    handler={(event) => requestAction("ITEM_DELETE")}
                                />
                            </ButtonGroupRow>
                        </AccessCheck>
                    </Fragment>
                )}
            </ButtonGroup>
        )
    }

    handleSelectionChange = (selectedItem) => {
        const { onFormDataChange } = this.props

        onFormDataChange("pricebook_item", selectedItem?.object)
    }

    render() {
        const {
            item,
            isNewAddition,
            onFormDataChange,
            requestAction,
            formatCurrencyValue,
            currencySymbol,
            pricebookItemType,
            errors,
        } = this.props

        const itemErrors = item.errors || {}
        const inputLabel = PRICEBOOK_ITEM_TYPE_MAP[item.pricebook_item?.pricebook_item_type] || '';
        const cannotEditPricebook = window.CURRENT_USER?.permissions.pricebook_edit_permission < PERMISSION_LEVEL.FULL
        const isPriceComputed = item.price_strategy !== PriceStrategies.manual

        return (
            <div className="data-panel__list-select__form">
                <div className="data-panel__list-select" aria-label="Item Edit Form">
                    {itemErrors.non_field_error && (
                        <div className="data-panel__form__field__errors" aria-label="Non Field Error">
                            {itemErrors.non_field_error}
                        </div>
                    )}
                    {
                        isNewAddition ? (
                            <SearchCreateSelectField
                                fieldName="pricebook_item"
                                fieldLabel={inputLabel}
                                fieldValue={item.pricebook_item?.description || null}
                                inputComponent={
                                    <PureComponentWrapper
                                        wrappedComponent={PriceBookItemSearchOrCreateSelect}
                                        updateTrigger={(currentProps, nextProps) =>
                                            nextProps.defaultSelected?.value !== currentProps.defaultSelected?.value
                                        }
                                        onSelectionChange={(selectedOption) => this.handleSelectionChange(selectedOption)}
                                        defaultSelected={
                                            this.state.selectedPriceBookItem
                                                ? priceBookItemToPriceBookItemOption(this.state.selectedPriceBookItem)
                                                : item.pricebook_item?.description
                                                  ? { value: null, label: item.pricebook_item?.description }
                                                  : null
                                        }
                                        pricebookItemType={pricebookItemType}
                                        showCreateButton={false}
                                        creatable={false}
                                        disabled={
                                            window.CURRENT_USER?.permissions.pricebook_list_permission < PERMISSION_LEVEL.RESTRICTED ||
                                            window.CURRENT_USER?.permissions.pricebook_view_permission < PERMISSION_LEVEL.FULL
                                        }
                                    ></PureComponentWrapper>
                                }
                                showButton={false}
                                errors={{...errors, ...itemErrors}}
                            ></SearchCreateSelectField>
                            ) : (
                            <BasicDisplayField
                                fieldName="pricebook_item"
                                fieldLabel={inputLabel}
                                fieldValue={item.pricebook_item?.description || "\u2014"}
                            ></BasicDisplayField>
                        )
                    }

                    <HorizontalLayout>
                        <BasicDisplayField
                            fieldName="default_price"
                            fieldLabel="Default Price"
                            fieldValue={
                                valueIsDefined(item.pricebook_item?.default_price)
                                    ? item.pricebook_item?.pricebook_item_type === PriceBookItemTypes.discount
                                        ? formatCurrencyValue(item.pricebook_item?.default_price * -1)
                                        : formatCurrencyValue(item.pricebook_item?.default_price)
                                    : "\u2014"
                            }
                        ></BasicDisplayField>
                        <BasicSelectField
                            fieldName="price_strategy"
                            fieldLabel="Price Strategy"
                            fieldValue={item.price_strategy}
                            fieldOnChange={price_strategy => onFormDataChange("price_strategy", parseInt(price_strategy))}
                            choices={PRICE_STRATEGY_CHOICES}
                            disabled={cannotEditPricebook}
                            errors={{...errors, ...itemErrors}}
                        ></BasicSelectField>
                    </HorizontalLayout>
                    {
                        item.price_strategy === PriceStrategies.fixed_adjustment && (
                            <PriceField
                                fieldName="price_adjustment_amount"
                                fieldLabel="Fixed Markup"
                                fieldValue={item.price_adjustment_amount || ""}
                                fieldOnChange={price_adjustment_amount => onFormDataChange("price_adjustment_amount", price_adjustment_amount || 0)}
                                currencySymbol={currencySymbol}
                                disabled={cannotEditPricebook}
                                min={null}
                                errors={{...errors, ...itemErrors}}
                            ></PriceField>
                        )
                    }
                    {
                        item.price_strategy === PriceStrategies.percent_adjustment && (
                            <PercentField
                                fieldName="price_adjustment_percent"
                                fieldLabel="Percent Markup"
                                fieldValue={item.price_adjustment_percent}
                                fieldOnChange={price_adjustment_percent => onFormDataChange("price_adjustment_percent", price_adjustment_percent || 0)}
                                disabled={cannotEditPricebook}
                                min="-100"
                                max={null}
                                errors={{...errors, ...itemErrors}}
                            ></PercentField>
                        )
                    }
                    <PriceField
                        key={isPriceComputed ? `computed-price-${item.price}` : "price"}
                        fieldName="price"
                        fieldLabel="Override Price"
                        fieldValue={item.price}
                        fieldOnChange={(price) => onFormDataChange("price", price)}
                        currencySymbol={currencySymbol}
                        credit={pricebookItemType === PriceBookItemTypes.discount}
                        errors={{...errors, ...itemErrors}}
                        optional={isPriceComputed}
                        optionalText="calculated"
                        disabled={cannotEditPricebook || isPriceComputed}
                    ></PriceField>
                    {this.renderButtons()}
                </div>
            </div>
        )
    }
}

export default InlinePriceOverrideForm
